import { FullScreenSpinner } from 'components/Elements';
import { Notifications } from 'components/Notifications/Notifications';
import { AuthProvider } from 'lib/auth';
import { queryClient } from 'lib/react-query';
import * as React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { BrowserRouter as Router, useNavigate } from 'react-router-dom';
import { ElectronProvider, useElectron } from 'context/ElectronContext';
import { DeviceRestriction } from './DeviceRestriction';
import { ErrorFallback } from './ErrorFallback';
import Cookies from 'js-cookie';
import { defaultHotKeys } from 'constants/defaultHotKeys';
import { useFirebaseMessaging } from 'hooks/useFirebaseMessaging';
import { FCMTokenManager } from 'managers';
import storage from 'utils/storage';
import { ThemeProvider } from './ThemeProvider';
import { CursorProvider } from './CursorProvider';
import { DeepLinkProvider } from './DeepLinkProvider';
import { UrlCryptoProvider } from './UrlCryptoProvider';
import { RoleProvider } from './Role';

const COOKIE_KEY = 'custom_shortcuts';

const ShortcutHotkeys = () => {
  const navigate = useNavigate();

  const flatShortcuts = React.useMemo(() => {
    let shortcuts = defaultHotKeys;
    const saved = Cookies.get(COOKIE_KEY);
    if (saved) {
      try {
        shortcuts = JSON.parse(saved);
      } catch (e) {
        console.error('Invalid shortcut cookie data', e);
      }
    }
    return Object.entries(shortcuts).reduce((acc, [group, keys]) => {
      Object.entries(keys).forEach(([key, value]) => {
        acc[key] = value;
      });
      return acc;
    }, {});
  }, []);

  React.useEffect(() => {
    const handleKeyDown = (e) => {
      const key = e.key;
      if ((e.metaKey || e.ctrlKey) && flatShortcuts[key]) {
        e.preventDefault();
        navigate(flatShortcuts[key].path);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [flatShortcuts, navigate]);

  return null;
};

export const AppProvider = ({ children }) => {
  const [isDesktop, setIsDesktop] = React.useState(false);
  const [standby, setStandby] = React.useState(false);

  const idleTimeoutRef = React.useRef(null);

  // ✅ 2. Check if device is desktop
  React.useEffect(() => {
    const checkDevice = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  // ✅ 3. Set standby mode ON after timeout, and DO NOT auto-exit
  React.useEffect(() => {
    const resetTimer = () => {
      if (standby) return;
      clearTimeout(idleTimeoutRef.current);
      idleTimeoutRef.current = setTimeout(() => {
        setStandby(true);
      }, 5 * 60 * 1000); // 5 minutes
    };

    const events = ['mousemove', 'keydown', 'mousedown', 'touchstart'];
    events.forEach((event) => window.addEventListener(event, resetTimer));

    resetTimer();

    return () => {
      events.forEach((event) => window.removeEventListener(event, resetTimer));
      clearTimeout(idleTimeoutRef.current);
    };
  }, [standby]);

  async function validateApproval({ reason, code }) {
    try {
      // ✅ Plug in your server here for real approval
      // const res = await fetch('/api/admin/approvals/validate', {
      // method: 'POST',
      // headers: { 'Content-Type': 'application/json' },
      // body: JSON.stringify({ reason, code }),
      // });
      // return res.ok;

      // Demo fallback (remove in production)
      return code === 'SUPER-OK';
    } catch (e) {
      console.error('validateApproval failed', e);
      return false;
    }
  }

  const isElectron = useElectron();

  useFirebaseMessaging();

  if (!isDesktop && !isElectron) {
    return <DeviceRestriction />;
  }

  return (
    <ElectronProvider>
      <ThemeProvider>
        <React.Suspense fallback={<FullScreenSpinner />}>
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <HelmetProvider>
              <QueryClientProvider client={queryClient}>
                {process.env.REACT_APP_NODE_ENV !== 'test' && (
                  <ReactQueryDevtools />
                )}
                <Notifications />
                <AuthProvider>
                  <RoleProvider validateApproval={validateApproval}>
                    <CursorProvider>
                      <Router>
                        <DeepLinkProvider>
                          <UrlCryptoProvider>
                            {!isElectron && (
                              <FCMTokenManager
                                onTokenReceived={(token) => {
                                  storage.setFCMToken(token);
                                  console.log('Received FCM token:', token);
                                }}
                              />
                            )}
                            <ShortcutHotkeys />
                            {children}
                          </UrlCryptoProvider>
                        </DeepLinkProvider>
                      </Router>
                    </CursorProvider>
                  </RoleProvider>
                </AuthProvider>
              </QueryClientProvider>
            </HelmetProvider>
          </ErrorBoundary>
        </React.Suspense>
      </ThemeProvider>
    </ElectronProvider>
  );
};
